import { CloseDialogFn, useDialog, UserInfo } from '@wui/common-library'
import { NameAddrHeader } from '@wui/jssip'
import { AnswerOptions, HoldOptions, RTCSession, SessionDirection, TerminateOptions } from '@wui/jssip/lib/RTCSession'
import { IncomingRequest } from '@wui/jssip/lib/SIPMessage'
import { ComponentOptions, computed, ComputedRef, Ref, ref, ShallowRef, shallowRef } from 'vue'

import { useGuestAuth } from '@/composables/useGuestAuth'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useIntercomRoomParticipantsStatusesStore } from '@/entities/room'

import { DialogWrapper } from '@/shared/components/dialogs'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useRoomControl } from '@/shared/composables/use-room-control'
import { sendDTMFMediaConstraintsToSession } from '@/shared/utils/jssip'

import { markAttendedTransferCompleted } from './attended-transfer-state'
import { ReferResult, ReferResultType } from './types'
import { useSessionEventListener } from './use-session-event-listener'
import { SessionTimer, useSessionTimer } from './use-session-timer'
import { useWebRTC } from './use-web-rtc'

export enum STATE {
  'INITIAL',
  'PROGRESS',
  'RINGING',
  'CONNECTED',
  'ERROR',
  'ONHOLD',
  'DISCONNECTED',
  'CANCELED',
  'REJECTED',
  'BUSY',
  'OFFLINE',
  'UNAVAILABLE'
}

export enum STATUS_CODES {
  BUSY_HERE = 486
}

type DialogProps = Parameters<ReturnType<typeof useDialog>['showDialog']>[1]

export interface RTCSessionFacade {
  session: RTCSession,
  sessionId: string,
  sessionState: Ref<STATE>,
  direction: SessionDirection;
  answer: (options: AnswerOptions) => void,
  terminate: (options?: TerminateOptions) => void,
  localAudioStream: ShallowRef<MediaStream | undefined>;
  localVideoStream: ShallowRef<MediaStream | undefined>;
  localScreenStream: ShallowRef<MediaStream | undefined>;
  isVideoSharing: ComputedRef<boolean>;
  isScreenSharing: ComputedRef<boolean>;
  dialog: {
    showDialog: () => void;
    closeDialog: Ref<CloseDialogFn<unknown> | undefined>,
  },
  number: string,
  displayName: Ref<string>,
  timer: SessionTimer,
  isMuted: Ref<boolean>,
  isVideoMuted: Ref<boolean>,
  isOnHold: ComputedRef<boolean>,
  sendDtmf: (dtmf: string) => void,
  toggleMute: () => void;
  muteAudio: () => void;
  unMuteAudio: () => void;
  toggleHold: () => void;
  toggleHoldInConference: () => void;
  onHoldInConference: () => void;
  refer: (to: string, onEnded: (result: ReferResult) => void, number?: string) => void;
  blindRefer: (to: string, onEnded: (result: ReferResult) => void) => void;
  setState: (s: STATE) => void;
  referToSessionId: Ref<string | null>;
  callId: Ref<string>;
  replaceOnProgress: Ref<boolean>; // Требуется для create group call from tet-a-tet-only
  isCallingDialogOpened: Ref<boolean>;
}

export const FIRST_STREAM_ID_SUFFIX = '-53454C46' // self by ASCII, used for detect MOH stream

function getDisplayName (number: string, name?: string) {
  return name ?? number
}

function getNumber (session: RTCSession) {
  const remoteIdentity = session.remote_identity
  const { uri } = remoteIdentity
  const { user } = uri
  return user
}

export function useRTCSessionFacade (session: RTCSession, request: IncomingRequest): RTCSessionFacade {

  const sessionFacade = (function (): RTCSessionFacade {
    const sessionRoomStore = useCallSessionRoomStore()
    const isScreenSharing = computed(() => Boolean(localScreenStream.value))
    const sessionState = ref<STATE>(STATE.INITIAL)
    const localAudioStream = shallowRef<MediaStream>()
    const localVideoStream = shallowRef<MediaStream>()
    const isVideoSharing = computed(() => !isScreenSharing.value && Boolean(localVideoStream.value?.getVideoTracks()?.find(({ enabled }) => enabled)))
    const localScreenStream = shallowRef<MediaStream>()

    const { showDialog: showDialogFn, closeDialog } = useDialog()
    const isCallingDialogOpened = ref(false)
    const showDialog = (component: ComponentOptions, options: DialogProps) => {
      isCallingDialogOpened.value = true
      showDialogFn(component, options).then(() => {
        isCallingDialogOpened.value = false
      })
    }

    const number = getNumber(session)
    const contactInfo = NameAddrHeader.parse(request.getHeader('Contact') || request.getHeader('From')) as unknown as NameAddrHeader
    const remoteContactName = contactInfo.display_name || session.remote_identity.display_name || number
    const room = sessionRoomStore.bindSession(session.id, number, remoteContactName)
    const displayName = ref(getDisplayName(number, room.name))
    const timer = useSessionTimer()
    const isMuted = ref<boolean>(!!session?.isMuted()?.audio)
    const isVideoMuted = ref<boolean>(!isVideoSharing.value)
    const callId = ref<string>('')

    const isOnHold = computed(() => {
      if (room?.isGroup) {
        const { currentUser } = useCurrentUser()
        const { currentGuest } = useGuestAuth()

        const user = currentUser.value as UserInfo
        let internalNumber = ''
        if (user) {
          internalNumber = user.internalNumber
        } else if (currentGuest.value) {
          internalNumber = currentGuest.value.pServed
        } else {
          return false
        }

        const { getParticipantStatus } = useIntercomRoomParticipantsStatusesStore()

        const memberStatus = getParticipantStatus(room.pServedUser, internalNumber, callId.value)
        if (memberStatus === 'OnHold') {
          return true
        } else if (memberStatus === 'Active') {
          return false
        } else {
          return false
        }
      } else {
        return sessionState.value === STATE.ONHOLD
      }
    })
    const referToSessionId = ref<string | null>(null)
    const replaceOnProgress = ref(false)

    function terminate (options?: TerminateOptions) {
      try {
        session.terminate(options)
      } catch { }
    }

    function answer (options: AnswerOptions) {
      session.answer(options)

      /**
       * Компенсация сигнала об отсутствии видео при вызовах
       * между двумя веб-клиентами
       */
      setTimeout(() => {
        const isLocalVideoEnabled = !!localVideoStream.value
        if (room && !room.isGroup && !isLocalVideoEnabled) {
          sendDTMFMediaConstraintsToSession(session, { video: isLocalVideoEnabled })
            .catch(() => {})
        }
      }, 1000)
    }

    function unMuteAudio () {
      if (session.isMuted().audio) {
        session.unmute({ video: false, audio: true })
      }
    }

    function muteAudio () {
      if (!session.isMuted().audio) {
        session.mute({ video: false, audio: true })
      }
    }

    function toggleMute () {
      if (session.isMuted().audio) {
        session.unmute({ video: false, audio: true })
      } else {
        session.mute({ video: false, audio: true })
      }
    }

    function toggleHold () {
      if (session.isOnHold().remote) {
        return
      }

      const options: HoldOptions = {
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
          iceRestart: false,
        },
      }

      if (session.isOnHold().local) {
        session.unhold(options, () => ({}), () => ({}))
      } else {
        session.hold(options, () => ({}), () => ({}))
      }
    }

    function onHoldInConference () {
      const { getParticipantStatus } = useIntercomRoomParticipantsStatusesStore()
      const { currentUser } = useCurrentUser()
      const { memberHold } = useRoomControl()
      if (!currentUser.value) return

      const memberStatus = getParticipantStatus(room.pServedUser, currentUser.value.internalNumber, callId.value)
      if (memberStatus === 'Active') {
        memberHold(room.pServedUser, currentUser.value.internalNumber, callId.value)
      }
    }

    function toggleHoldInConference () {
      const { getParticipantStatus } = useIntercomRoomParticipantsStatusesStore()
      const { currentUser } = useCurrentUser()
      const { memberHold, memberUnHold } = useRoomControl()
      const { setActiveSession } = useWebRTC()
      if (!currentUser.value) return
      const memberStatus = getParticipantStatus(room.pServedUser, currentUser.value.internalNumber, callId.value)
      if (memberStatus === 'OnHold') {
        memberUnHold(room.pServedUser, currentUser.value.internalNumber, callId.value)
        setActiveSession(sessionFacade.sessionId)
      } else if (memberStatus === 'Active') {
        memberHold(room.pServedUser, currentUser.value.internalNumber, callId.value)
      }
    }

    function blindRefer (to: string, onEnded: (result: ReferResult) => void) {
      const { t } = useLocalization()
      const eventHandlers = {
        requestSucceeded: function () {
          onEnded({
            type: ReferResultType.REQUEST_SUCCESS,
            message: t('common.blindReferSuccessful', { to }),
          })
        },
        requestFailed: function () {
          onEnded({
            type: ReferResultType.REQUEST_FAIL,
            message: t('common.blindReferError', { to }),
          })
        },
      }
      session.refer(to, { eventHandlers })
    }

    function refer (to: string, onEnded: (result: ReferResult) => void, number?: string): void {
      const { getRTCSessionFacadeById } = useWebRTC()
      if (!sessionFacade.referToSessionId.value) return
      const referSessionFacade = getRTCSessionFacadeById(sessionFacade.referToSessionId.value)
      if (!referSessionFacade) return
      const { t } = useLocalization()
      const toNumber = number ?? to
      const eventHandlers = {
        requestSucceeded: function () {
          onEnded({
            type: ReferResultType.REQUEST_SUCCESS,
            message: t('common.numberTransferSuccessful', { to: toNumber }),
          })
        },
        requestFailed: function () {
          onEnded({
            type: ReferResultType.REQUEST_FAIL,
            message: t('common.numberTransferError', { to: toNumber }),
          })
        },
        trying: function () {
          onEnded({
            type: ReferResultType.TRYING,
            message: t('common.tryNumberTransfer', { to: toNumber }),
          })
        },
        progress: function () {
          onEnded({
            type: ReferResultType.PROGRESS,
            message: t('common.numberTransferInProgress', { to: toNumber }),
          })
        },
        accepted: function () {
          onEnded({
            type: ReferResultType.ACCEPTED,
            message: t('common.numberTransferAccepted', { to: toNumber }),
          })
          markAttendedTransferCompleted(sessionFacade.sessionId)
          terminate()
          referSessionFacade.terminate()
        },
        failed: function () {
          onEnded({
            type: ReferResultType.FAILED,
            message: t('common.numberTransferFail', { to: toNumber }),
          })
        },
      }
      session.refer(to, { eventHandlers, replaces: referSessionFacade.session })
    }

    function setState (state: STATE): void {
      sessionState.value = state
    }

    function sendDtmf (dtmf: string): void {
      session.sendDTMF(dtmf, { duration: 200, interToneGap: 500 })
    }

    return {
      session,
      sessionState,
      referToSessionId,
      callId,
      direction: session.direction,
      answer,
      terminate,
      toggleHold,
      sessionId: session.id,
      number,
      displayName,
      isVideoSharing,
      isScreenSharing,
      localAudioStream,
      localVideoStream,
      localScreenStream,
      dialog: {
        closeDialog,
        showDialog: () => showDialog(DialogWrapper as unknown as ComponentOptions, { sessionId: session.id, hasOverlay: true }),
      },
      timer,
      isMuted,
      isVideoMuted,
      isOnHold,
      replaceOnProgress,
      isCallingDialogOpened,
      toggleMute,
      muteAudio,
      unMuteAudio,
      refer,
      blindRefer,
      setState,
      sendDtmf,
      toggleHoldInConference,
      onHoldInConference,
    }
  })()

  const eventListener = useSessionEventListener(sessionFacade)
  eventListener.addEventListeners()

  return sessionFacade
}
